import { useState, useEffect, Fragment } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  Plus, Pencil, Trash2, Shield, User, Phone, Building2,
  Check, X, Eye, EyeOff, Loader2, Link2, Copy, QrCode,
  Search, ChevronDown, ChevronLeft, ChevronRight, Filter
} from "lucide-react";
import { DeleteConfirmModal } from "../components/DeleteConfirmModal";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";

// Icon map for each permission category header
const CATEGORY_ICONS = {
  "Members": "👥",
  "Memberships": "💳",
  "Packages": "📦",
  "Leads": "🎯",
  "Attendance": "✅",
  "Reports & Data": "📊",
  "Administration": "🔧",
  "WhatsApp": "💬",
  "Workouts": "🏋️",
  "Calendar": "📅",
};

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 640px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

export default function Staff() {
  const { branches, organization, user } = useAuth();
  const isDesktop = useIsDesktop();

  const [staffList, setStaffList] = useState([]);
  const [permissionCategories, setPermissionCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", permissions: [], branchId: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showPerms, setShowPerms] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showLoginPage, setShowLoginPage] = useState(false);
  const [orgSlug, setOrgSlug] = useState("");
  const [slugLoading, setSlugLoading] = useState(false);

  // Search, filter & pagination state
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, fullAccess: 0, restricted: 0, allBranches: 0 });
  const [expandedRowId, setExpandedRowId] = useState(null);

  useEffect(() => {
    if (organization?.slug) { setOrgSlug(organization.slug); return; }
    if (organization?.id || user?.organizationId) {
      setSlugLoading(true);
      api.get("/auth/me")
        .then((res) => { const slug = res.data?.organization?.slug; if (slug) setOrgSlug(slug); })
        .catch(() => { })
        .finally(() => setSlugLoading(false));
    }
  }, [organization?.slug, organization?.id, user?.organizationId]);

  const loginUrl = orgSlug ? `${window.location.origin}/org/${orgSlug}/login` : "";

  useEffect(() => {
    fetchPermissions();
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [page, limit, searchQuery, branchFilter]);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const res = await api.get("/staff", {
        params: {
          name: searchQuery,
          branchId: branchFilter,
          page,
          limit,
        },
      });
      setStaffList(res.data.staff || []);
      setTotal(res.data.total || 0);
      if (res.data.stats) {
        setStats(res.data.stats);
      }
    }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchPermissions = async () => {
    try { const res = await api.get("/staff/permissions"); setPermissionCategories(res.data.categories); }
    catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    if (!editing && !form.password) { toast.error("Password required"); return; }
    setSubmitting(true);
    try {
      if (editing) {
        await api.put(`/staff/${editing.id}`, { name: form.name, phone: form.phone, permissions: form.permissions, branchId: form.branchId });
        toast.success("Staff updated");
      } else {
        await api.post("/staff", form);
        toast.success("Staff created");
      }
      setShowForm(false); setEditing(null);
      setForm({ name: "", email: "", password: "", phone: "", permissions: [], branchId: "" });
      setShowPassword(false); fetchStaff();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/staff/${deleteTarget.id}`);
      toast.success("Staff deleted"); setShowDeleteModal(false); setDeleteTarget(null); fetchStaff();
    } catch { toast.error("Failed"); }
    finally { setDeleteLoading(false); }
  };

  const togglePermission = (perm) =>
    setForm((p) => ({ ...p, permissions: p.permissions.includes(perm) ? p.permissions.filter((x) => x !== perm) : [...p.permissions, perm] }));

  const toggleCategoryPermissions = (categoryPerms) => {
    const allKeys = categoryPerms.map((p) => p.key);
    const allSelected = allKeys.every((k) => form.permissions.includes(k));
    if (allSelected) setForm((p) => ({ ...p, permissions: p.permissions.filter((k) => !allKeys.includes(k)) }));
    else setForm((p) => ({ ...p, permissions: [...new Set([...p.permissions, ...allKeys])] }));
  };

  const savePermissions = async () => {
    try {
      await api.put(`/staff/${showPerms.id}/permissions`, { permissions: form.permissions });
      toast.success("Permissions updated"); setShowPerms(null); fetchStaff();
    } catch { toast.error("Failed"); }
  };

  const getAllPermissionKeys = () => Object.values(permissionCategories).flat().map((p) => p.key);
  const selectAllPermissions = () => setForm((p) => ({ ...p, permissions: getAllPermissionKeys() }));
  const clearAllPermissions = () => setForm((p) => ({ ...p, permissions: [] }));
  const getBranchName = (id) => branches.find((b) => b.id === id)?.name || "All Branches";
  const getPermissionCount = (s) => s.permissions?.length || 0;

  const openAdd = () => {
    setEditing(null); setForm({ name: "", email: "", password: "", phone: "", permissions: [], branchId: "" });
    setShowPassword(false); setShowForm(true);
  };
  const openEdit = (s) => {
    setEditing(s); setForm({ name: s.name, email: s.email, password: "", phone: s.phone || "", permissions: s.permissions || [], branchId: s.branchId || "" });
    setShowPassword(false); setShowForm(true);
  };
  const copyLoginUrl = () => { navigator.clipboard.writeText(loginUrl); toast.success("Login URL copied!"); };

  const statCards = [
    { label: "Total Staff", value: stats.total, icon: User },
    { label: "Full Access", value: stats.fullAccess, icon: Shield, color: "text-emerald-500" },
    { label: "Restricted", value: stats.restricted, icon: Shield, color: "text-amber-500" },
    { label: "All Branches", value: stats.allBranches, icon: Building2, color: "text-blue-500" },
  ];

  const paginatedStaff = staffList;
  const totalPages = Math.ceil(total / limit) || 1;
  const activeFilterCount = (searchQuery ? 1 : 0) + (branchFilter !== "all" ? 1 : 0);

  const renderStaffDetails = (s) => {
    return (
      <div className="flex flex-col bg-secondary/10">
        <div className="flex items-center justify-between gap-2 p-2 border-b border-border bg-secondary/20">
          <div className="text-xs text-muted-foreground font-mono hidden md:block">
            Staff ID: {s.id}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
              onClick={(e) => { e.stopPropagation(); setShowPerms(s); setForm((p) => ({ ...p, permissions: s.permissions || [] })); }}
              data-testid={`perms-staff-${s.id}`}>
              <Shield className="h-3.5 w-3.5" /> Permissions
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
              onClick={(e) => { e.stopPropagation(); openEdit(s); }}>
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
            <Button variant="ghost" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide text-red-500 hover:text-red-600 hover:bg-red-500/10 ml-auto"
              onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); setShowDeleteModal(true); }}>
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
          <div className="space-y-3">
            <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground">General Info</p>
            <div className="bg-background/50 rounded-lg p-3 space-y-2 border border-border/50 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email:</span>
                <span className="font-mono">{s.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Phone:</span>
                <span className="font-mono">{s.phone || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Branch:</span>
                <span>{getBranchName(s.branchId)}</span>
              </div>
              {s.createdAt && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created At:</span>
                  <span>{s.createdAt.slice(0, 10)}</span>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-3">
            <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground">Assigned Permissions ({getPermissionCount(s)})</p>
            <div className="bg-background/50 rounded-lg p-3 border border-border/50 max-h-[160px] overflow-y-auto [scrollbar-width:thin]">
              {s.permissions && s.permissions.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {s.permissions.map((pKey) => {
                    let label = pKey;
                    for (const categoryPerms of Object.values(permissionCategories)) {
                      const match = categoryPerms.find((p) => p.key === pKey);
                      if (match) {
                        label = match.label;
                        break;
                      }
                    }
                    return (
                      <Badge key={pKey} variant="outline" className="text-[10px] py-0 px-2 font-sans font-normal">
                        {label}
                      </Badge>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">No permissions assigned.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-2 mt-2 md:space-y-3" data-testid="staff-page">
      <div className="flex sm:flex-row sm:items-center justify-between gap-2 md:gap-4 ">
        <div>
          <h1 className="text-xl md:text-4xl font-black font-heading tracking-tight uppercase">Staff Members</h1>
        </div>
        <div className="flex gap-2">
          <Button variant={activeFilterCount > 0 ? "secondary" : "outline"} size="sm"
            onClick={() => setShowFilters(true)} className="font-heading uppercase tracking-wide text-xs" data-testid="toggle-filters-btn">
            <Filter className="h-4 w-4" /> <span className="hidden sm:block">Filters</span>
            {activeFilterCount > 0 && (
              <Badge className="bg-primary text-primary-foreground h-4 min-w-4 px-1 rounded-full text-[10px]">{activeFilterCount}</Badge>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowLoginPage(true)} className="font-heading uppercase tracking-wide text-xs" data-testid="staff-login-page-btn">
            <QrCode className="h-4 w-4" /> <span className="hidden sm:block">Login Page</span>
          </Button>
          <Button size="sm" onClick={openAdd} className="font-heading uppercase tracking-wide text-xs" data-testid="add-staff-btn">
            <Plus className="h-4 w-4 animate-pulse" /> <span className="">Add Staff</span>
          </Button>
        </div>
      </div>

      <div className="flex overflow-x-auto gap-2 pb-1 -mx-4 px-4 md:grid md:grid-cols-4 md:gap-3 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {statCards.map((s, i) => (
          <Card key={i} className="bg-card border-border group hover:border-primary/40 transition-all min-w-[105px] md:min-w-0 flex-1 shrink-0">
            <CardContent className="p-2.5 sm:p-4">
              <div className="flex items-center gap-1 sm:gap-2">
                <s.icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${s.color || "text-muted-foreground"} group-hover:text-primary transition-colors shrink-0`} />
                <p className="text-[8px] sm:text-[10px] text-muted-foreground font-mono uppercase tracking-wider sm:tracking-widest leading-tight truncate">{s.label}</p>
              </div>
              <p className={`text-base sm:text-2xl font-black font-heading mt-1 sm:mt-1.5 ${s.color || ""}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Sheet open={showFilters} onOpenChange={setShowFilters}>
        <SheetContent
          side={isDesktop ? "right" : "bottom"}
          className={isDesktop
            ? "w-[400px] flex flex-col p-0 bg-card border-border"
            : "rounded-t-2xl flex flex-col p-0 max-h-[92vh] bg-card border-border"
          }
        >
          <SheetHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-heading uppercase tracking-wide text-base flex items-center gap-2">
                <Filter className="h-4 w-4" /> Filters
                {activeFilterCount > 0 && (
                  <Badge className="bg-primary text-primary-foreground text-[10px] px-1.5">{activeFilterCount} active</Badge>
                )}
              </SheetTitle>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(""); setBranchFilter("all"); setPage(1); }} className="text-muted-foreground text-xs h-7">
                  <X className="h-3.5 w-3.5 mr-1" />Clear all
                </Button>
              )}
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Name, email, phone..." value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="pl-9" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Branch</Label>
              <Select value={branchFilter} onValueChange={(v) => { setBranchFilter(v); setPage(1); }}>
                <SelectTrigger><SelectValue placeholder="All Branches" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-border bg-card shrink-0">
            <Button className="w-full font-heading uppercase tracking-wide text-xs" onClick={() => setShowFilters(false)}>
              Apply Filters
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Card className="bg-card border-x-0 md:border border-y border-border overflow-hidden flex flex-col flex-1 min-h-0  md:mx-0 rounded-lg">

        <div className="hidden md:block overflow-auto flex-1">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10"></TableHead>
                <TableHead>Staff Member</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Permissions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12">
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
                    <span className="text-muted-foreground text-sm">Loading...</span>
                  </div>
                </TableCell></TableRow>
              ) : paginatedStaff.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">No staff members found</TableCell></TableRow>
              ) : paginatedStaff.map((s) => (
                <Fragment key={s.id}>
                  <TableRow
                    className="group cursor-pointer hover:bg-secondary/50"
                    data-testid={`staff-row-${s.id}`}
                    onClick={() => setExpandedRowId(expandedRowId === s.id ? null : s.id)}
                  >
                    <TableCell className="w-10">
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <ChevronDown className={`h-4 w-4 transition-transform ${expandedRowId === s.id ? "rotate-180" : ""}`} />
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9 border border-border">
                          <AvatarFallback className="bg-primary/10 text-primary text-xs font-heading font-bold">{s.name?.[0]?.toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm leading-tight">{s.name}</p>
                          <p className="text-[10px] text-muted-foreground font-mono">{s.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{s.phone || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        <Building2 className="h-3 w-3 mr-1 text-primary/70" />
                        {getBranchName(s.branchId)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getPermissionCount(s) > 0 ? "default" : "secondary"} className="text-xs font-mono">
                        {getPermissionCount(s)} perms
                      </Badge>
                    </TableCell>
                  </TableRow>

                  {expandedRowId === s.id && (
                    <TableRow className="bg-secondary/30 hover:bg-secondary/30 border-b border-border animate-in fade-in duration-200">
                      <TableCell colSpan={5} className="p-0">
                        {renderStaffDetails(s)}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="md:hidden overflow-x-hidden overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
              <span className="text-muted-foreground text-sm ml-2">Loading...</span>
            </div>
          ) : paginatedStaff.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No staff members found</div>
          ) : paginatedStaff.map((s, idx) => (
            <Collapsible
              key={s.id}
              open={expandedRowId === s.id}
              onOpenChange={() => setExpandedRowId(expandedRowId === s.id ? null : s.id)}
              asChild
            >
              <div data-testid={`staff-card-${s.id}`}
                className={` border-b border-border hover:bg-secondary/10 transition-colors`}>
                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between gap-2 cursor-pointer select-none px-2 py-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="flex items-center justify-between flex-col">
                        <Avatar className="h-9 w-9 border border-border">
                          <AvatarFallback className="bg-primary/10 text-primary text-xs font-heading font-bold">{s.name?.[0]?.toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <Button variant="ghost" size="icon" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground shrink-0">
                          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expandedRowId === s.id ? "rotate-180" : ""}`} />
                        </Button>
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm leading-tight truncate">{s.name}</p>
                        <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{s.email}</p>
                        <div className="flex items-center gap-1.5 mt-1 text-[11px] font-mono text-muted-foreground">
                          <Building2 className="h-3 w-3 shrink-0 text-primary/70" />
                          <span>{getBranchName(s.branchId)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant={getPermissionCount(s) > 0 ? "default" : "secondary"} className="text-[9px] font-mono uppercase py-0 px-1.5">
                        {getPermissionCount(s)} Perms
                      </Badge>
                    </div>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className=" border-t border-border/50 animate-in fade-in slide-in-from-top-1 duration-200">
                    {renderStaffDetails(s)}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>

        <div className="flex flex-row items-center justify-between gap-4 p-4 border-t border-border">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="font-mono hidden sm:block">{total} total</span>
            <div className="flex items-center gap-2">
              <span className="text-xs">Show:</span>
              <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
                <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="sm:text-sm text-xs text-muted-foreground px-2">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page * limit >= total} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </Card>

      <Dialog open={showLoginPage} onOpenChange={setShowLoginPage}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <QrCode className="h-5 w-5 text-primary" />Staff Login Page
            </DialogTitle>
            <DialogDescription>Share this link or QR code with your staff so they can sign in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {slugLoading ? (
              <div className="flex flex-col items-center gap-2 py-6">
                <Loader2 className="h-7 w-7 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Building your login URL…</p>
              </div>
            ) : loginUrl ? (
              <>
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Login URL</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 flex items-center gap-2 h-10 rounded-sm border border-input bg-secondary px-3 overflow-hidden">
                      <Link2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate text-xs font-mono">{loginUrl}</span>
                    </div>
                    <Button variant="outline" size="icon" className="h-10 w-10 rounded-sm shrink-0" onClick={copyLoginUrl}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" className="h-10 w-10 rounded-sm shrink-0" asChild>
                      <a href={loginUrl} target="_blank" rel="noreferrer"><Eye className="h-4 w-4" /></a>
                    </Button>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-3 py-2">
                  <div className="p-3 bg-white rounded-lg border border-border">
                    <QRCodeSVG value={loginUrl} size={200} level="M" includeMargin={false} />
                  </div>
                  <p className="text-xs text-muted-foreground text-center">Staff can scan this QR code to open the login page.</p>
                </div>
              </>
            ) : (
              <div className="py-4 text-center">
                <p className="text-sm text-muted-foreground">Could not load your login URL. Please refresh and try again.</p>
              </div>
            )}
            <Button variant="outline" className="w-full rounded-sm" onClick={() => setShowLoginPage(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showForm} onOpenChange={(open) => { if (!submitting) setShowForm(open); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "Add"} Staff Member</DialogTitle>
            <DialogDescription>{editing ? "Update staff member details" : "Create a new staff account"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="rounded-sm mt-1" data-testid="staff-name" disabled={submitting} />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} disabled={!!editing || submitting} className="rounded-sm mt-1" data-testid="staff-email" />
            </div>
            {!editing && (
              <div>
                <Label>Password *</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                    className="rounded-sm pr-10" data-testid="staff-password" disabled={submitting}
                  />
                  <button type="button" tabIndex={-1} onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}
            <div>
              <Label>Phone</Label>
              <PhoneInput defaultCountry="in"
                value={form.phone} onChange={(v) => setForm((p) => ({ ...p, phone: v || "" }))}
                disabled={submitting}
                inputClassName="flex h-10 w-full rounded-sm border border-input bg-secondary px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-primary/30"
                className="mt-1"
                data-testid="staff-phone"
              />
            </div>
            <div>
              <Label>Assigned Branch</Label>
              <Select value={form.branchId || "all"} onValueChange={(v) => setForm((p) => ({ ...p, branchId: v === "all" ? "" : v }))} disabled={submitting}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="staff-branch"><SelectValue placeholder="All Branches" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Staff will only see data from their assigned branch</p>
            </div>
            <Button className="w-full rounded-sm" onClick={handleSave} disabled={submitting} data-testid="save-staff-btn">
              {submitting
                ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{editing ? "Updating…" : "Creating…"}</span>
                : (editing ? "Update Staff" : "Create Staff")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showPerms} onOpenChange={() => setShowPerms(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />Permissions — {showPerms?.name}
            </DialogTitle>
            <DialogDescription>Configure what this staff member can access and manage</DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between py-2 border-b border-border">
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{form.permissions.length}</span> of{" "}
              <span className="font-semibold text-foreground">{getAllPermissionKeys().length}</span> permissions selected
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={selectAllPermissions}>
                <Check className="h-3 w-3 mr-1" />Select All
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={clearAllPermissions}>
                <X className="h-3 w-3 mr-1" />Clear All
              </Button>
            </div>
          </div>

          <ScrollArea className="h-[440px] pr-3">
            <div className="space-y-0 py-1">
              {Object.entries(permissionCategories).map(([category, permissions], idx) => {
                const categoryKeys = permissions.map((p) => p.key);
                const selectedCount = categoryKeys.filter((k) => form.permissions.includes(k)).length;
                const allSelected = selectedCount === categoryKeys.length && categoryKeys.length > 0;
                const icon = CATEGORY_ICONS[category] || "🔑";
                return (
                  <div
                    key={category}
                    className="mt-5 first:mt-0"
                  >
                    <div className="flex items-center justify-between mb-3 px-1">
                      <div className="flex items-center gap-2">
                        <span className="text-base" aria-hidden>{icon}</span>
                        <h3 className="font-semibold text-sm">{category}</h3>
                        <Badge
                          variant={selectedCount > 0 ? "default" : "secondary"}
                          className="rounded-full text-[10px] px-2 h-4"
                        >
                          {selectedCount}/{categoryKeys.length}
                        </Badge>
                      </div>
                      <Switch
                        checked={allSelected}
                        onCheckedChange={() => toggleCategoryPermissions(permissions)}
                        data-testid={`toggle-category-${category.replace(/\s+/g, "-").toLowerCase()}`}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {permissions.map((perm) => (
                        <label
                          key={perm.key}
                          className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50 cursor-pointer hover:bg-secondary transition-colors"
                        >
                          <Checkbox
                            checked={form.permissions.includes(perm.key)}
                            onCheckedChange={() => togglePermission(perm.key)}
                            className="mt-0.5"
                            data-testid={`perm-${perm.key}`}
                          />
                          <div>
                            <p className="text-sm font-medium leading-tight">{perm.label}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{perm.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>

                    {idx < Object.keys(permissionCategories).length - 1 && (
                      <Separator className="mt-5" />
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <div className="flex justify-end gap-2 pt-3 border-t border-border">
            <Button variant="outline" className="rounded-sm" onClick={() => setShowPerms(null)}>Cancel</Button>
            <Button className="rounded-sm" onClick={savePermissions} data-testid="save-perms-btn">Save Permissions</Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmModal
        open={showDeleteModal}
        onOpenChange={() => { if (!deleteLoading) { setShowDeleteModal(false); setDeleteTarget(null); } }}
        onConfirm={handleDelete}
        title="Delete Staff Member"
        description="They will no longer be able to access the system."
        itemName={deleteTarget?.name}
        loading={deleteLoading}
      />
    </div>
  );
}
